import {
  ActionError,
  AttentionBlock,
  Button,
  ConfirmDialog,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  SavedIndicator,
  Select,
  Skeleton,
  Surface,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { personNameError, placeFieldErrors } from '@/features/clinic-profile/form'
import {
  focusFirstInvalid,
  useFormState,
  type FormErrors,
} from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  usePractitioner,
  usePractitionerAction,
  usePractitionerUsage,
  useProfessionalCouncils,
  useSavePractitioner,
  type Practitioner,
  type PractitionerInput,
  type ProfessionalCouncil,
} from './api'
import { REGISTRATION_PATTERN, archiveEffect, councilLabel, normalizeRegistration } from './labels'

/**
 * Criar e editar prestador (docs/areas/administracao.md, "Editar prestador"; RN-ADM-20 a 24).
 * Arquétipo: cadastro. Conselho e profissão são listas ligadas pela tabela do sistema: a tela só
 * oferece as profissões do conselho escolhido, e é isso que impede "Fisioterapeuta com CRM" de
 * chegar ao documento assinado. Arquivar fica num cartão à parte; não há excluir.
 */

interface FormState {
  name: string
  council: string
  profession: string
  specialty: string
  registrationNumber: string
}

const EMPTY: FormState = {
  name: '',
  council: '',
  profession: '',
  specialty: '',
  registrationNumber: '',
}

const KEYS = ['name', 'council', 'profession', 'specialty', 'registrationNumber'] as const

function fromPractitioner(practitioner: Practitioner): FormState {
  return {
    // O nome vai para o documento em caixa de título: o campo mostra como ele sai.
    name: titleCase(practitioner.name),
    council: practitioner.council,
    profession: practitioner.profession,
    specialty: practitioner.specialty ?? '',
    registrationNumber: practitioner.registrationNumber ?? '',
  }
}

function registrationMessage(council: ProfessionalCouncil) {
  return `Informe a região e o número do registro, como ${council.registrationExample ?? 'SP 897886'}.`
}

/** As mesmas regras da API (RN-ADM-20 e 21), conferidas contra a tabela conselho × profissão. */
function validate(values: FormState, councils: ProfessionalCouncil[]): FormErrors<FormState> {
  const errors: FormErrors<FormState> = {}
  const nameError = personNameError(values.name)
  if (nameError) errors.name = nameError

  const council = councils.find((item) => item.code === values.council)
  if (!council) {
    errors.council = 'Escolha um conselho da lista.'
    return errors
  }
  if (!council.professions.some((item) => item.code === values.profession)) {
    errors.profession = 'Escolha uma profissão da lista.'
  }
  if (
    council.requiresRegistration &&
    !REGISTRATION_PATTERN.test(normalizeRegistration(values.registrationNumber))
  ) {
    errors.registrationNumber = registrationMessage(council)
  }
  return errors
}

function toInput(values: FormState, council: ProfessionalCouncil | undefined): PractitionerInput {
  return {
    name: titleCase(values.name),
    council: values.council,
    profession: values.profession,
    specialty: values.specialty.trim() || null,
    // Sem conselho não há registro: vai `null` (RN-ADM-21).
    registrationNumber: council?.requiresRegistration
      ? normalizeRegistration(values.registrationNumber)
      : null,
  }
}

/** O campo do formulário que recebe o erro de um campo da API (422). */
function fieldKey(path: string): keyof FormState | undefined {
  return (KEYS as readonly string[]).includes(path) ? (path as keyof FormState) : undefined
}

export function PractitionerPage() {
  const { id } = useParams()
  const query = usePractitioner(id)
  // O formulário nasce do cadastro carregado; outro id, outro formulário.
  return <PractitionerForm key={query.data?.id ?? 'new'} id={id} query={query} />
}

function PractitionerForm({
  id,
  query,
}: {
  id: string | undefined
  query: ReturnType<typeof usePractitioner>
}) {
  const isNew = !id
  const navigate = useNavigate()
  const councilsQuery = useProfessionalCouncils()
  const save = useSavePractitioner(id)
  const action = usePractitionerAction()
  const practitioner = query.data
  const isArchived = Boolean(practitioner?.archivedAt)

  const councils = councilsQuery.data ?? []
  const form = useFormState<FormState>(
    () => (practitioner ? fromPractitioner(practitioner) : EMPTY),
    (values) => validate(values, councils),
  )
  const { values } = form
  const formRef = useRef<HTMLFormElement>(null)
  const [archiving, setArchiving] = useState(false)
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)
  // Os números do uso só são pedidos quando o modal de arquivar abre.
  const usage = usePractitionerUsage(archiving && practitioner ? practitioner.id : null)

  const council = councils.find((item) => item.code === values.council)
  const professions = council?.professions ?? []
  // Um par que não está na tabela (cadastro antigo) aparece como "não escolhido", nunca como válido.
  const profession = professions.some((item) => item.code === values.profession)
    ? values.profession
    : ''
  const hasRegistration = council?.requiresRegistration ?? true
  const displayName = practitioner ? titleCase(practitioner.name) : ''

  function changeCouncil(code: string) {
    const next = councils.find((item) => item.code === code)
    form.update({
      council: code,
      // Trocar o conselho limpa a profissão: ela precisa ser uma das dele (RN-ADM-20).
      profession: '',
      registrationNumber: next && !next.requiresRegistration ? '' : values.registrationNumber,
    })
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (isArchived || !councilsQuery.data || (!isNew && !practitioner)) return
    if (!form.check(KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    const input = toInput(values, council)
    save.mutate(input, {
      onSuccess: () => {
        toast(
          isNew
            ? `${input.name} cadastrada — entra nos próximos documentos`
            : `Cadastro de ${input.name} salvo — vale nos próximos documentos`,
        )
        navigate(routes.admin.practitioners)
      },
      onError: (error) => {
        form.refuse(placeFieldErrors<FormState>(error, fieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  function runAction(kind: 'archive' | 'restore') {
    if (!practitioner) return
    setFailure(null)
    action.mutate(
      { id: practitioner.id, action: kind },
      {
        onSuccess: () => {
          if (kind === 'archive') {
            toast(`Cadastro de ${displayName} arquivado — volta a qualquer momento`)
            navigate(routes.admin.practitioners)
          } else toast(`Cadastro de ${displayName} restaurado — volta à lista de profissionais`)
        },
        onError: (error) => {
          setArchiving(false)
          setFailure({
            title:
              kind === 'archive'
                ? `Não foi possível arquivar "${displayName}"`
                : `Não foi possível restaurar "${displayName}"`,
            error,
          })
        },
      },
    )
  }

  const refused = save.isError ? placeFieldErrors<FormState>(save.error, fieldKey) : null
  const showRefused =
    refused !== null && (Object.keys(refused.fields).length === 0 || refused.unplaced.length > 0)
  const conflict =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as { id: string; isArchived: boolean } | undefined)
      : undefined

  const loadFailed = !isNew && query.isError
  const isLoading = !loadFailed && (councilsQuery.isPending || (!isNew && query.isPending))
  const isReady = !loadFailed && !isLoading && !councilsQuery.isError

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Prestadores', href: routes.admin.practitioners },
          { label: isNew ? 'novo' : displayName || '…' },
        ]}
      />
      <Page>
        <ReadingColumn>
          <PageTitle
            title={isNew ? 'Novo prestador' : 'Editar prestador'}
            subtitle="Estes três dados aparecem no documento assinado pelo paciente."
          />

          {loadFailed ? (
            <LoadError
              className="mt-6"
              title="Não conseguimos abrir este cadastro"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : councilsQuery.isError ? (
            <LoadError
              className="mt-6"
              title="Não conseguimos carregar a lista de conselhos"
              error={councilsQuery.error}
              onRetry={() => void councilsQuery.refetch()}
            />
          ) : isLoading ? (
            <div role="status" aria-label="Carregando" className="mt-6 flex flex-col gap-4">
              <Skeleton className="h-[330px] w-full" />
              <Skeleton className="h-[96px] w-full" />
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {isArchived ? (
                <AttentionBlock variant="lilac" title="Este cadastro está arquivado">
                  Não aparece na lista de profissionais nem pode ser escolhido em novos documentos;
                  os documentos já emitidos continuam intactos. Restaure para editar.
                </AttentionBlock>
              ) : null}

              {showRefused ? (
                <ActionError
                  title="Não foi possível salvar o cadastro"
                  description={
                    refused.unplaced.length > 0
                      ? refused.unplaced.join(' ')
                      : `${errorMessage(save.error)} O que você digitou continua na tela.`
                  }
                  actions={
                    conflict ? (
                      <Button variant="outline" size="sm" asChild>
                        <Link to={routes.admin.practitioner(conflict.id)}>
                          {conflict.isArchived ? 'Ver o arquivado' : 'Abrir o existente'}
                        </Link>
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              {failure ? (
                <ActionError
                  title={failure.title}
                  description={`${errorMessage(failure.error)} Nada mudou no cadastro.`}
                  onDismiss={() => setFailure(null)}
                />
              ) : null}

              <fieldset disabled={isArchived} className="flex min-w-0 flex-col gap-4">
                <FormBlock
                  title="Identificação profissional"
                  note="Aparece no resumo, na qualificação das partes e na assinatura de cada documento."
                >
                  <FieldGrid>
                    <Field
                      className="col-span-full"
                      label="Nome completo"
                      error={form.errors.name}
                      hint={
                        form.errors.name
                          ? undefined
                          : 'Sai no documento em caixa de título, mesmo digitado em minúsculas.'
                      }
                    >
                      <Input
                        value={values.name}
                        maxLength={120}
                        autoComplete="off"
                        onChange={(event) => form.update({ name: event.target.value })}
                        onBlur={() => {
                          if (values.name.trim() === '') return
                          const cased = titleCase(values.name)
                          if (cased !== values.name) form.update({ name: cased })
                          form.touch('name')
                        }}
                      />
                    </Field>

                    <Field
                      label="Conselho"
                      error={form.errors.council}
                      hint={council && councilLabel(council) !== council.name ? council.name : null}
                    >
                      <Select
                        placeholder="Selecione"
                        value={council ? values.council : ''}
                        onChange={(event) => changeCouncil(event.target.value)}
                      >
                        {councils.map((item) => (
                          <option key={item.code} value={item.code}>
                            {councilLabel(item)}
                          </option>
                        ))}
                      </Select>
                    </Field>

                    <Field label="Profissão" error={form.errors.profession}>
                      <Select
                        placeholder={council ? 'Selecione' : 'Escolha o conselho primeiro'}
                        disabled={!council}
                        value={profession}
                        onChange={(event) => form.update({ profession: event.target.value })}
                      >
                        {professions.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                      </Select>
                    </Field>

                    <Field
                      label="Registro profissional"
                      error={form.errors.registrationNumber}
                      hint={
                        form.errors.registrationNumber
                          ? undefined
                          : !hasRegistration
                            ? 'Sem conselho não há registro.'
                            : `Região e número, como ${council?.registrationExample ?? 'SP 897886'}.`
                      }
                    >
                      <Input
                        value={hasRegistration ? values.registrationNumber : ''}
                        disabled={!hasRegistration}
                        maxLength={30}
                        autoComplete="off"
                        autoCapitalize="characters"
                        onChange={(event) =>
                          form.update({ registrationNumber: event.target.value.toUpperCase() })
                        }
                        onBlur={() => {
                          if (values.registrationNumber.trim() === '') return
                          const normalized = normalizeRegistration(values.registrationNumber)
                          if (normalized !== values.registrationNumber) {
                            form.update({ registrationNumber: normalized })
                          }
                          form.touch('registrationNumber')
                        }}
                      />
                    </Field>

                    <Field
                      label="Especialidade"
                      optional
                      error={form.errors.specialty}
                      hint={
                        form.errors.specialty ? undefined : 'Sai no documento depois da profissão.'
                      }
                    >
                      <Input
                        value={values.specialty}
                        maxLength={80}
                        autoComplete="off"
                        onChange={(event) => form.update({ specialty: event.target.value })}
                      />
                    </Field>
                  </FieldGrid>
                </FormBlock>
              </fieldset>

              <AttentionBlock variant="lilac">
                Conselho e profissão são listas ligadas: escolher CRM limita as profissões a
                médicas. Evita "Fisioterapeuta com CRM" chegando ao contrato.
              </AttentionBlock>

              {isReady ? (
                <FormFooter
                  status={
                    practitioner && !isArchived ? (
                      <SavedIndicator
                        savedAt={new Date(practitioner.updatedAt)}
                        dirty={form.dirty}
                        saving={save.isPending}
                      />
                    ) : null
                  }
                  secondary={
                    <Button variant="outline" asChild>
                      <Link to={routes.admin.practitioners}>Cancelar</Link>
                    </Button>
                  }
                  primary={
                    <Button
                      type="submit"
                      disabled={isArchived}
                      loading={save.isPending}
                      loadingLabel="Salvando…"
                    >
                      {isArchived ? 'Restaure para editar' : isNew ? 'Cadastrar' : 'Salvar'}
                    </Button>
                  }
                />
              ) : null}

              {practitioner ? (
                <Surface className="flex flex-wrap items-center justify-between gap-4">
                  <div className="max-w-[520px]">
                    <div className="font-display text-lead text-ink leading-[1.3] font-normal">
                      {isArchived ? 'Restaurar prestador' : 'Arquivar prestador'}
                    </div>
                    <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
                      {isArchived
                        ? 'Volta à lista de profissionais e à escolha em novos documentos, com os mesmos dados.'
                        : practitioner.user
                          ? `${titleCase(practitioner.user.name)} entra no sistema com este cadastro. Bloqueie ou remova o usuário antes de arquivar.`
                          : 'Sai da lista de profissionais e das escolhas novas. O que já foi emitido continua intacto, e o cadastro volta a qualquer momento.'}
                    </p>
                  </div>
                  {isArchived ? (
                    <Button
                      variant="outline"
                      loading={action.isPending}
                      loadingLabel="Restaurando…"
                      onClick={() => runAction('restore')}
                    >
                      Restaurar
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={Boolean(practitioner.user)}
                      onClick={() => setArchiving(true)}
                    >
                      {practitioner.user ? 'Há um usuário ativo ligado' : 'Arquivar'}
                    </Button>
                  )}
                </Surface>
              ) : null}
            </div>
          )}
        </ReadingColumn>
      </Page>

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        tone="primary"
        title={`Arquivar "${displayName}"?`}
        effect={archiveEffect(usage.data)}
        confirmLabel="Arquivar"
        loading={action.isPending}
        loadingLabel="Arquivando…"
        onConfirm={() => runAction('archive')}
      />
    </form>
  )
}
